/**
 * CMS'dagi portal kontenti va sozlamalarini zaxira kontent bilan birlashtiradi.
 *
 * Qoida bitta: **backend qiymati ustun**, u bo'sh bo'lsa `content.ts` dagi
 * zaxira ishlatiladi. Shu sababli sahifa backendsiz ham to'liq ko'rinadi,
 * backend ulanganda esa telefon, e-pochta va murojaat turlari CMS'da
 * tahrirlanadigan qiymatlarga o'tadi (ЧТЗ 4.2.11.1).
 *
 * `GET /api/v1/portal/public/` bir sahifada bir necha komponentga kerak
 * bo'lgani uchun so'rov modul darajasida keshlanadi — takroriy so'rov
 * yuborilmaydi.
 */
import { useEffect, useState } from "react";

import { portalApi } from "@/shared/api/endpoints";
import type { Multilingual, PublicHome } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";

import {
  ADDRESS,
  appealTypes as fallbackAppealTypes,
  HOTLINE,
  SUPPORT_EMAIL,
  WORK_HOURS,
} from "./content";
import type { AppealType } from "./content";

/** Ko'p tilli qiymatni joriy tilga keltiradi (satr ham, lug'at ham qabul qilinadi). */
export function translate(value: Multilingual | undefined, lang: string): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return value[lang] || value.uz || value.ru || value.en || "";
  }
  return "";
}

const homeCache = new Map<string, Promise<PublicHome>>();

function loadHome(lang: string): Promise<PublicHome> {
  let request = homeCache.get(lang);
  if (!request) {
    request = portalApi.home(lang);
    // Xatolikda kesh tozalanadi, aks holda bir marta uzilgan aloqa sessiya
    // oxirigacha «yopishib» qolardi.
    request.catch(() => homeCache.delete(lang));
    homeCache.set(lang, request);
  }
  return request;
}

/** Bosh sahifa kontenti (bannerlar, yangiliklar, xizmat havolalari). */
export function usePortalHome(): PublicHome | null {
  const { lang } = useI18n();
  const [home, setHome] = useState<PublicHome | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadHome(lang)
      .then((data) => {
        if (!cancelled) setHome(data);
      })
      .catch(() => {
        /* backend ulanmagan — zaxira kontent ko'rsatiladi */
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return home;
}

export interface PortalConfig {
  hotline: string;
  email: string;
  address: string;
  workHours: string;
  /** ЧТЗ 4.1.1.3 — texnologik tanaffus rejimi. */
  maintenance: boolean;
  maintenanceMessage: string;
}

/** Rasmiy rekvizitlar va portal rejimi (`PortalSettings` + zaxira qiymatlar). */
export function usePortalConfig(): PortalConfig {
  const { lang } = useI18n();
  const settings = usePortalHome()?.settings;

  return {
    hotline: settings?.hotline_phone || HOTLINE,
    email: settings?.support_email || SUPPORT_EMAIL,
    address: translate(settings?.address, lang) || ADDRESS,
    workHours: WORK_HOURS,
    maintenance: Boolean(settings?.maintenance_mode),
    maintenanceMessage: translate(settings?.maintenance_message, lang),
  };
}

/**
 * Publik murojaat turlari — `EventType.appeal_visible` (rang va nom backenddan).
 * SLA normativi bu endpointda yo'q, shuning uchun u zaxira ro'yxatdan olinadi.
 */
export function useAppealTypes(): AppealType[] {
  const { lang } = useI18n();
  const [types, setTypes] = useState<AppealType[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    portalApi
      .appealTypes(lang)
      .then((data) => {
        if (cancelled || !data?.length) return;
        const slaByCode = new Map(fallbackAppealTypes.map((item) => [item.code, item.sla]));
        setTypes(
          data.map((item) => ({
            code: item.code,
            color: item.color,
            title: translate(item.name, lang),
            sla: slaByCode.get(item.code) ?? "",
          })),
        );
      })
      .catch(() => {
        /* backend ulanmagan — zaxira ro'yxat ko'rsatiladi */
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return types ?? fallbackAppealTypes;
}
